import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { CRM_THEMES } from "../context/SalonContext";
import { maskCNPJ, maskCPF, maskPhone, isValidEmail } from "../lib/format";
import { clearRouteHint, peekRouteHint, returnHomeIfRequested } from "../lib/setupRoute";
import api from "../lib/api";
import BrandStudio from "./BrandStudio";
import AIAgentSection from "./AIAgentSection";
import ImageCropperModal from "../components/ImageCropperModal";

/**
 * Account (Configurações) — Módulo de gestão da própria conta do tenant.
 * Navegação em cards de tela cheia (hub → seção), mesmo padrão do Painel
 * Financeiro/Catálogo — sem tabs horizontais, que quebravam no mobile.
 *
 * 5 seções:
 *   1. Perfil do Salão (identidade do negócio: tipo/documento, nome, logo, endereço)
 *   2. Horários de Funcionamento (link de agendamento, colaboradores, grade semanal)
 *   3. Agente IA & Números Internos
 *   4. Temas (aparência do painel + Estúdio de Marca da página pública)
 *   5. Minha Conta (dados de contato do proprietário, PINs, plano & pagamento)
 *
 * NOTA de reorganização: os campos de "Dados Cadastrais" e "Configurações do
 * Salão" originais foram redistribuídos entre Perfil do Salão / Minha Conta
 * (identidade do NEGÓCIO vs. dados de CONTATO do proprietário) — cada campo,
 * validação e chamada a updateBrand() continua exatamente como antes, só
 * mudou em qual tela cada um aparece.
 */
const NavCard = ({ testId, icon, title, description, onClick }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className="rounded-2xl border border-white/8 bg-slate-900/40 hover:border-amber-500/40 hover:bg-amber-500/5 backdrop-blur-xl p-5 text-left transition-all group flex flex-col gap-3 w-full"
    >
        <div className="flex items-center justify-between">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20">
                <i className={`fa-solid ${icon} text-amber-400 text-xl`}></i>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-amber-300 group-hover:translate-x-1 transition-all"></i>
        </div>
        <div>
            <div className="font-semibold text-white text-sm">{title}</div>
            <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
    </button>
);

const BackButton = ({ onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all"
    >
        <i className="fa-solid fa-arrow-left"></i>
    </button>
);

const SETTINGS_CARDS = [
    { id: "profile", title: "Perfil do Salão", icon: "fa-store", desc: "Nome, logo, endereço e informações básicas" },
    { id: "hours", title: "Horários de Funcionamento", icon: "fa-clock", desc: "Configure quando seu salão está aberto" },
    { id: "ai", title: "Agente IA & Números Internos", icon: "fa-robot", desc: "Configure o assistente automático do WhatsApp" },
    { id: "themes", title: "Temas", icon: "fa-palette", desc: "Personalize as cores do seu painel" },
    { id: "account", title: "Minha Conta", icon: "fa-user", desc: "Dados do proprietário e senha" },
];

const Account = () => {
    const { brand, updateBrand, professionals, setCrmTheme, setActiveModule } = useSalon();
    // "hub" | "profile" | "hours" | "ai" | "themes" | "account"
    // May be deep-linked from the Home setup checklist (logo → profile, horários → hours).
    // The initializer only READS the hint (pure — safe under StrictMode's double
    // invoke); the one-shot removal happens in the effect below.
    const [view, setView] = useState(() => peekRouteHint("kinng.route.account.view") || "hub");
    useEffect(() => {
        clearRouteHint("kinng.route.account.view");
    }, []);

    // Back button on a section: return to Home when the checklist asked for it, else the hub.
    const goBack = useCallback(() => {
        if (!returnHomeIfRequested(() => setActiveModule("home"))) setView("hub");
    }, [setActiveModule]);

    // After saving inside a section (e.g. business hours): ONLY navigate when the
    // checklist deep-linked the user here — a normal edit stays on the screen.
    const handleSectionSaved = useCallback(() => {
        returnHomeIfRequested(() => setActiveModule("home"));
    }, [setActiveModule]);

    if (view === "hub") {
        return (
            <div data-testid="account-hub" className="max-w-5xl mx-auto space-y-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Configurações</div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Configurações da Conta</h1>
                    <p className="text-sm text-slate-400 mt-1">Gerencie o perfil, horários, agente IA e mais do seu salão.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {SETTINGS_CARDS.map((c) => (
                        <NavCard
                            key={c.id}
                            testId={`account-card-${c.id}`}
                            icon={c.icon}
                            title={c.title}
                            description={c.desc}
                            onClick={() => setView(c.id)}
                        />
                    ))}
                </div>
            </div>
        );
    }

    const card = SETTINGS_CARDS.find((c) => c.id === view);
    const isThemes = view === "themes"; // Estúdio de Marca precisa de largura cheia (sem max-w-5xl)

    return (
        <div data-testid={`account-view-${view}`} className={isThemes ? "space-y-6" : "max-w-5xl mx-auto space-y-6"}>
            <div className="flex items-center gap-3">
                <BackButton onClick={goBack} testId="account-back-btn" />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Configurações</div>
                    <h2 className="salon-heading text-2xl text-white mt-0.5">{card?.title}</h2>
                </div>
            </div>

            {view === "profile" && <ProfileSection brand={brand} updateBrand={updateBrand} onSaved={handleSectionSaved} />}
            {view === "hours" && <HoursSection brand={brand} updateBrand={updateBrand} professionals={professionals} onSaved={handleSectionSaved} />}
            {view === "ai" && <AIAgentSection />}
            {view === "themes" && <ThemesSection currentTheme={brand.crmTheme || "vintage"} setCrmTheme={setCrmTheme} />}
            {view === "account" && <MyAccountSection brand={brand} updateBrand={updateBrand} />}
        </div>
    );
};

// ─── Section: Aparência do CRM ──────────────────────────────────────────────
const AppearancePreview = ({ colors }) => (
    <div
        className="w-full aspect-[16/10] rounded-xl overflow-hidden border border-white/5 flex"
        style={{ background: colors.body }}
    >
        {/* Fake sidebar */}
        <div className="w-[30%] p-2 flex flex-col gap-1.5" style={{ background: colors.sidebar }}>
            <div className="h-3 rounded" style={{ background: colors.sidebarText, opacity: 0.35, width: "60%" }}></div>
            <div className="h-2 rounded mt-1" style={{ background: colors.accent, width: "80%", opacity: 0.9 }}></div>
            <div className="h-2 rounded" style={{ background: colors.sidebarText, opacity: 0.25, width: "70%" }}></div>
            <div className="h-2 rounded" style={{ background: colors.sidebarText, opacity: 0.25, width: "50%" }}></div>
            <div className="h-2 rounded" style={{ background: colors.sidebarText, opacity: 0.25, width: "65%" }}></div>
        </div>
        {/* Fake content */}
        <div className="flex-1 p-3 flex flex-col gap-2">
            <div className="h-2.5 rounded" style={{ background: colors.text, opacity: 0.7, width: "40%" }}></div>
            <div className="flex-1 rounded-lg mt-1" style={{ background: colors.card }}></div>
        </div>
    </div>
);

const AppearanceSection = ({ currentTheme, setCrmTheme }) => {
    const [applyingId, setApplyingId] = useState(null);

    const handleApply = async (themeId) => {
        setApplyingId(themeId);
        try {
            await setCrmTheme(themeId);
            const theme = CRM_THEMES.find((t) => t.id === themeId);
            toast.success(`Tema "${theme?.name}" aplicado!`, {
                description: "O visual do painel foi atualizado.",
            });
        } catch (e) {
            toast.error("Erro ao aplicar tema", { description: e.message });
        } finally {
            setApplyingId(null);
        }
    };

    return (
        <div data-testid="appearance-section" className="space-y-6">
            <div className="rounded-3xl border border-white/5 bg-slate-900/40 p-6 lg:p-8">
                <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
                            <i className="fa-solid fa-swatchbook text-indigo-400"></i>
                            Personalização Visual
                        </div>
                        <h2 className="salon-heading text-2xl text-white mt-1">Escolha o visual do seu sistema</h2>
                        <p className="text-sm text-slate-400 mt-1.5 max-w-2xl">
                            Este tema afeta apenas o painel interno de gestão do seu salão. A identidade pública da sua loja (landing, agendamento online, redes)
                            é configurada logo abaixo, no <span className="text-white font-medium">Estúdio de Marca</span>.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {CRM_THEMES.map((theme) => {
                        const active = currentTheme === theme.id;
                        const busy = applyingId === theme.id;
                        return (
                            <div
                                key={theme.id}
                                data-testid={`theme-card-${theme.id}`}
                                className={`relative rounded-2xl p-4 border transition-all duration-300 ${
                                    active
                                        ? "border-amber-400/60 bg-amber-400/5 shadow-[0_0_0_1px_rgba(217,119,6,0.35)]"
                                        : "border-white/10 hover:border-white/25 bg-slate-950/50"
                                }`}
                            >
                                {active && (
                                    <span
                                        data-testid={`theme-active-badge-${theme.id}`}
                                        className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/40"
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                                        Ativo
                                    </span>
                                )}

                                <AppearancePreview colors={theme.preview} />

                                <div className="mt-3">
                                    <div className="text-sm font-semibold text-white">{theme.name}</div>
                                    <div className="text-xs text-slate-400 mt-0.5 min-h-[32px]">{theme.description}</div>
                                </div>

                                <button
                                    data-testid={`theme-apply-${theme.id}`}
                                    disabled={active || busy}
                                    onClick={() => handleApply(theme.id)}
                                    className={`mt-3 w-full min-h-[40px] rounded-xl text-xs font-semibold uppercase tracking-[0.2em] transition-all ${
                                        active
                                            ? "bg-white/5 text-slate-500 cursor-default"
                                            : "bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg"
                                    }`}
                                >
                                    {busy ? (
                                        <><i className="fa-solid fa-circle-notch fa-spin mr-1.5"></i>Aplicando...</>
                                    ) : active ? (
                                        <><i className="fa-solid fa-check mr-1.5"></i>Em uso</>
                                    ) : (
                                        <>Aplicar</>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-4 flex items-start gap-3">
                    <i className="fa-solid fa-lightbulb text-indigo-300 mt-0.5"></i>
                    <div className="text-xs text-indigo-100/90 leading-relaxed">
                        <span className="font-semibold">Dica:</span> a mudança acontece imediatamente sem recarregar a página.
                        A sua preferência fica salva na sua conta e será aplicada em todos os dispositivos ao fazer login.
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Section: Temas (Aparência do CRM + Estúdio de Marca) ──────────────────
const ThemesSection = ({ currentTheme, setCrmTheme }) => (
    <div className="space-y-6">
        <AppearanceSection currentTheme={currentTheme} setCrmTheme={setCrmTheme} />
        <BrandStudio />
    </div>
);

const WEEKDAY_LABELS = [
    { id: "mon", label: "Segunda" },
    { id: "tue", label: "Terça" },
    { id: "wed", label: "Quarta" },
    { id: "thu", label: "Quinta" },
    { id: "fri", label: "Sexta" },
    { id: "sat", label: "Sábado" },
    { id: "sun", label: "Domingo" },
];

const DEFAULT_HOURS = {
    mon: { open: true, from: "09:00", to: "20:00" },
    tue: { open: true, from: "09:00", to: "20:00" },
    wed: { open: true, from: "09:00", to: "20:00" },
    thu: { open: true, from: "09:00", to: "20:00" },
    fri: { open: true, from: "09:00", to: "21:00" },
    sat: { open: true, from: "09:00", to: "18:00" },
    sun: { open: false, from: "10:00", to: "16:00" },
};

// ─── Section: Perfil do Salão (identidade do negócio) ──────────────────────
const ProfileSection = ({ brand, updateBrand, onSaved }) => {
    const [pendingLogoFile, setPendingLogoFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef(null);
    const [draft, setDraft] = useState({
        ownerKind: brand.ownerKind || "PJ",
        ownerDocument: brand.ownerDocument || "",
        shopName: brand.shopName || "",
        logoUrl: brand.logoUrl || "",
    });
    const [address, setAddress] = useState(brand.salonAddress || {
        zip: "", street: "", number: "", complement: "", district: "", city: "", state: "",
    });

    const isPJ = draft.ownerKind === "PJ";

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const setAddrField = (k, v) => setAddress((a) => ({ ...a, [k]: v }));

    const handleSave = async () => {
        if (saving) return;
        const docDigits = (draft.ownerDocument || "").replace(/\D/g, "");
        if (isPJ && docDigits.length !== 14) {
            toast.error("CNPJ precisa ter 14 dígitos.");
            return;
        }
        if (!isPJ && docDigits.length !== 11) {
            toast.error("CPF precisa ter 11 dígitos.");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/tenant/settings", {
                shop_name: (draft.shopName || "").trim() || undefined,
                brand: {
                    ownerKind: draft.ownerKind,
                    ownerDocument: draft.ownerDocument,
                    logoUrl: draft.logoUrl,
                    salonAddress: address,
                },
            });
            updateBrand({ ...draft, salonAddress: address });
            toast.success("Perfil do salão atualizado", { description: "Dados salvos com sucesso." });
            onSaved?.();
        } catch (e) {
            toast.error("Não conseguimos salvar o perfil", {
                description: e?.message || "Tente novamente em instantes.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5">
            <Card title="Dados Cadastrais" desc="Mantenha seu cadastro atualizado para emissão de NF e atendimento ao cliente." icon="fa-id-card">
                {/* Tipo */}
                <div>
                    <Label>Tipo de cadastro</Label>
                    <div className="grid grid-cols-2 gap-3 max-w-md">
                        {[
                            { id: "PF", label: "Pessoa Física", icon: "fa-user", desc: "CPF" },
                            { id: "PJ", label: "Pessoa Jurídica", icon: "fa-building", desc: "CNPJ" },
                        ].map((k) => {
                            const active = draft.ownerKind === k.id;
                            return (
                                <button
                                    key={k.id}
                                    data-testid={`account-kind-${k.id}`}
                                    onClick={() => {
                                        setField("ownerKind", k.id);
                                        setField("ownerDocument", "");
                                    }}
                                    className={`min-h-[64px] flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                                        active
                                            ? "border-amber-500/60 bg-amber-500/5"
                                            : "border-white/10 hover:border-white/30 bg-slate-950/40"
                                    }`}
                                >
                                    <i className={`fa-solid ${k.icon} ${active ? "text-amber-400" : "text-slate-400"}`}></i>
                                    <div className="text-left">
                                        <div className="text-sm font-semibold text-white">{k.label}</div>
                                        <div className="text-[11px] text-slate-500">{k.desc}</div>
                                    </div>
                                    {active && <i className="fa-solid fa-check ml-auto text-amber-400"></i>}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <FieldInput
                        label={isPJ ? "CNPJ" : "CPF"}
                        testId="account-doc"
                        value={draft.ownerDocument}
                        onChange={(v) => setField("ownerDocument", isPJ ? maskCNPJ(v) : maskCPF(v))}
                        placeholder={isPJ ? "00.000.000/0000-00" : "000.000.000-00"}
                        mono
                    />
                    <FieldInput
                        label="Nome fantasia / Razão social"
                        testId="account-shop-name"
                        value={draft.shopName}
                        onChange={(v) => setField("shopName", v)}
                    />
                </div>

                {/* Logo uploader */}
                <div>
                    <Label>Logo do salão</Label>
                    <div className="mt-2 flex items-center gap-4 rounded-xl border border-white/10 bg-slate-950/50 p-4">
                        <div className="h-20 w-20 rounded-xl border border-amber-500/25 bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
                            {draft.logoUrl ? (
                                <img data-testid="account-logo-preview" src={draft.logoUrl} alt="Logo" className="h-full w-full object-contain" />
                            ) : (
                                <i className="fa-solid fa-image text-slate-600 text-2xl"></i>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 leading-relaxed">
                                PNG ou JPG, até 2 MB. Aparece na sidebar, recibos digitais, WhatsApp e link público de agendamento.
                            </p>
                            <div className="flex items-center gap-2 mt-3">
                                <label
                                    data-testid="account-logo-upload-label"
                                    className="min-h-[40px] px-4 rounded-lg border border-amber-500/40 hover:border-amber-400 bg-amber-500/5 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold cursor-pointer inline-flex items-center gap-2 transition-all"
                                >
                                    <i className="fa-solid fa-upload"></i>
                                    {draft.logoUrl ? "Trocar logo" : "Enviar logo"}
                                    <input
                                        ref={logoInputRef}
                                        data-testid="account-logo-file"
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = ""; // permite selecionar o mesmo arquivo de novo
                                            if (!file) return;
                                            if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
                                                toast.error("Envie uma imagem PNG, JPG ou WEBP.");
                                                return;
                                            }
                                            if (file.size > 2 * 1024 * 1024) {
                                                toast.error("Arquivo maior que 2 MB.");
                                                return;
                                            }
                                            setPendingLogoFile(file);
                                        }}
                                    />
                                </label>
                                {draft.logoUrl && (
                                    <button
                                        type="button"
                                        data-testid="account-logo-remove"
                                        onClick={() => setField("logoUrl", "")}
                                        className="min-h-[40px] px-3 rounded-lg border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-2 transition-all"
                                    >
                                        <i className="fa-solid fa-trash"></i>
                                        Remover
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {pendingLogoFile && (
                    <ImageCropperModal
                        file={pendingLogoFile}
                        onConfirm={(jpeg) => {
                            setField("logoUrl", jpeg);
                            setPendingLogoFile(null);
                            toast.success("Logo ajustado", { description: "Clique em Salvar para aplicar." });
                        }}
                        onCancel={() => setPendingLogoFile(null)}
                        onChangePhoto={() => { setPendingLogoFile(null); logoInputRef.current?.click(); }}
                    />
                )}
            </Card>

            {/* Endereço */}
            <Card
                title="Endereço do salão"
                desc="Aparece no rodapé das comunicações, recibos digitais e link de agendamento público."
                icon="fa-location-dot"
            >
                <div className="grid md:grid-cols-6 gap-4">
                    <FormField label="CEP" className="md:col-span-2">
                        <input
                            data-testid="salon-zip"
                            value={address.zip}
                            onChange={(e) => setAddrField("zip", e.target.value.replace(/\D/g, "").slice(0, 8))}
                            placeholder="00000-000"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60 font-mono"
                        />
                    </FormField>
                    <FormField label="Estado" className="md:col-span-1">
                        <input
                            data-testid="salon-state"
                            value={address.state}
                            onChange={(e) => setAddrField("state", e.target.value.toUpperCase().slice(0, 2))}
                            placeholder="SP"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60 uppercase"
                        />
                    </FormField>
                    <FormField label="Cidade" className="md:col-span-3">
                        <input
                            data-testid="salon-city"
                            value={address.city}
                            onChange={(e) => setAddrField("city", e.target.value)}
                            placeholder="Ex.: São Paulo"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </FormField>
                    <FormField label="Rua / Avenida" className="md:col-span-4">
                        <input
                            data-testid="salon-street"
                            value={address.street}
                            onChange={(e) => setAddrField("street", e.target.value)}
                            placeholder="Ex.: Av. Paulista"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </FormField>
                    <FormField label="Número" className="md:col-span-1">
                        <input
                            data-testid="salon-number"
                            value={address.number}
                            onChange={(e) => setAddrField("number", e.target.value)}
                            placeholder="123"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </FormField>
                    <FormField label="Bairro" className="md:col-span-3">
                        <input
                            data-testid="salon-district"
                            value={address.district}
                            onChange={(e) => setAddrField("district", e.target.value)}
                            placeholder="Bela Vista"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </FormField>
                    <FormField label="Complemento" className="md:col-span-3">
                        <input
                            data-testid="salon-complement"
                            value={address.complement}
                            onChange={(e) => setAddrField("complement", e.target.value)}
                            placeholder="Sala 101 · Bloco B"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </FormField>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-3 pt-2">
                <button
                    data-testid="account-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                    className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"} mr-2`}></i>
                    {saving ? "Salvando…" : "Salvar alterações"}
                </button>
            </div>
        </div>
    );
};

// ─── Section: Horários de Funcionamento ────────────────────────────────────
const HoursSection = ({ brand, updateBrand, professionals, onSaved }) => {
    const shopSlug = (brand.shopId || "salao").toLowerCase();
    const bookingBase = (typeof window !== "undefined" && window.location && window.location.origin) || "https://kinng.app";
    const bookingLink = `${bookingBase}/book/${shopSlug}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(bookingLink)}`;
    const copyLink = () => {
        try {
            navigator.clipboard.writeText(bookingLink);
            toast.success("Link copiado", { description: bookingLink });
        } catch (_) { toast.info("Copie manualmente: " + bookingLink); }
    };
    const [hours, setHours] = useState(brand.salonHours || DEFAULT_HOURS);
    const [saving, setSaving] = useState(false);

    const totalPros = professionals.length;
    const activePros = professionals.filter((p) => p.active !== false).length;
    const inactivePros = totalPros - activePros;
    const openDaysPerWeek = WEEKDAY_LABELS.filter((d) => hours[d.id]?.open).length;

    const setHour = (dayId, patch) =>
        setHours((h) => ({ ...h, [dayId]: { ...h[dayId], ...patch } }));

    const handleSave = async () => {
        if (saving) return;
        for (const d of WEEKDAY_LABELS) {
            const h = hours[d.id];
            if (!h?.open) continue;
            if (h.from >= h.to) {
                toast.error(`${d.label}: horário inválido (abertura ≥ fechamento)`);
                return;
            }
        }
        setSaving(true);
        try {
            // Persiste no backend (tenant.brand.salonHours) e marca que o passo
            // "configurar horários" foi concluído — mesmo que o lojista tenha
            // mantido a grade padrão sugerida.
            await api.patch("/tenant/settings", {
                brand: { salonHours: hours, salonHoursConfigured: true },
            });
            // Reflete imediatamente no contexto pra o checklist da Home reler o
            // estado novo sem depender de um syncTenantData().
            updateBrand({ salonHours: hours, salonHoursConfigured: true });
            toast.success("Horários atualizados", {
                description: `${openDaysPerWeek} dia(s) abertos por semana`,
            });
            onSaved?.();
        } catch (e) {
            toast.error("Não conseguimos salvar os horários", {
                description: e?.message || "Tente novamente em instantes.",
            });
        } finally {
            setSaving(false);
        }
    };

    const copyMondayToAll = () => {
        const ref = hours.mon;
        if (!ref) return;
        const next = {};
        for (const d of WEEKDAY_LABELS) next[d.id] = { ...ref };
        setHours(next);
        toast.success("Horário replicado para todos os dias");
    };

    return (
        <div className="space-y-6">
            {/* Link de Agendamento */}
            <div
                data-testid="salon-booking-link"
                className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.06] to-black/40 p-5 flex flex-col md:flex-row gap-5"
            >
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70">Booking Online</div>
                    <h3 className="salon-heading text-xl text-amber-100 mt-0.5">Link de Agendamento</h3>
                    <p className="text-xs text-amber-200/50 mt-1">
                        Divulgue no Instagram, WhatsApp e Google Meu Negócio. Solicitações caem direto na Agenda com status &quot;aguardando confirmação&quot;.
                    </p>
                    <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/60 px-3 py-2 font-mono text-xs text-amber-100 break-all">
                        <i className="fa-solid fa-link text-amber-300/60"></i>
                        <span className="flex-1 truncate" data-testid="salon-booking-link-value">{bookingLink}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button
                            data-testid="salon-booking-link-copy"
                            onClick={copyLink}
                            className="min-h-[40px] px-3 rounded-lg border border-amber-500/40 hover:border-amber-400 bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-2"
                        >
                            <i className="fa-solid fa-copy"></i>Copiar link
                        </button>
                        <a
                            data-testid="salon-booking-link-qr-download"
                            href={qrUrl}
                            download={`kinng-${shopSlug}-booking-qr.png`}
                            className="min-h-[40px] px-3 rounded-lg border border-white/15 hover:border-amber-400/60 text-slate-200 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-2"
                        >
                            <i className="fa-solid fa-download"></i>Baixar QR Code
                        </a>
                    </div>
                </div>
                <div className="shrink-0 flex items-center justify-center">
                    <div className="rounded-2xl border-2 border-amber-400/50 bg-white p-2 shadow-[0_20px_40px_-15px_rgba(212,175,55,0.5)]">
                        <img
                            data-testid="salon-booking-link-qr"
                            src={qrUrl}
                            alt="QR Code do link de agendamento"
                            className="h-32 w-32"
                        />
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="salon-kpis">
                <Kpi
                    icon="fa-users"
                    label="Colaboradores cadastrados"
                    value={totalPros}
                    accent="amber"
                    testId="kpi-total-pros"
                />
                <Kpi
                    icon="fa-user-check"
                    label="Ativos hoje"
                    value={activePros}
                    accent="emerald"
                    testId="kpi-active-pros"
                />
                <Kpi
                    icon="fa-user-slash"
                    label="Inativos"
                    value={inactivePros}
                    accent="slate"
                    testId="kpi-inactive-pros"
                />
                <Kpi
                    icon="fa-calendar-day"
                    label="Dias abertos / semana"
                    value={openDaysPerWeek}
                    accent="amber"
                    testId="kpi-open-days"
                />
            </div>

            {/* Horário de funcionamento */}
            <Card
                title="Horário de funcionamento"
                desc="Define a grade visível na Agenda e bloqueia novos agendamentos fora do expediente."
                icon="fa-clock"
            >
                <div className="flex items-center justify-end mb-1">
                    <button
                        data-testid="salon-copy-monday"
                        onClick={copyMondayToAll}
                        className="text-[10px] uppercase tracking-wider font-bold text-amber-300 hover:text-amber-200 transition-colors px-2.5 py-1.5 rounded-lg border border-amber-500/20 hover:border-amber-500/50"
                    >
                        <i className="fa-solid fa-copy mr-1.5"></i>Copiar Segunda p/ todos
                    </button>
                </div>
                <div className="space-y-1.5">
                    {WEEKDAY_LABELS.map((d) => {
                        const h = hours[d.id] || { open: false, from: "09:00", to: "18:00" };
                        return (
                            <div
                                key={d.id}
                                data-testid={`salon-hours-${d.id}`}
                                className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 transition-colors ${
                                    h.open ? "border-amber-500/20 bg-black/40" : "border-white/5 bg-slate-900/30"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    data-testid={`salon-toggle-${d.id}`}
                                    checked={h.open}
                                    onChange={() => setHour(d.id, { open: !h.open })}
                                    aria-label={`${d.label} — aberto`}
                                    className="h-4 w-4 shrink-0 cursor-pointer accent-emerald-500"
                                />
                                <span className={`w-14 shrink-0 text-xs font-semibold ${h.open ? "text-white" : "text-slate-500"}`}>
                                    {d.label}
                                </span>
                                {h.open ? (
                                    <div className="flex min-w-0 flex-1 items-center gap-1.5">
                                        <input
                                            data-testid={`salon-from-${d.id}`}
                                            type="time"
                                            value={h.from}
                                            onChange={(e) => setHour(d.id, { from: e.target.value })}
                                            className="min-w-0 flex-1 rounded-lg bg-black/60 border border-amber-500/20 px-1.5 py-1.5 text-xs text-white outline-none focus:border-amber-400/60 font-mono"
                                        />
                                        <span className="shrink-0 text-xs text-slate-500">–</span>
                                        <input
                                            data-testid={`salon-to-${d.id}`}
                                            type="time"
                                            value={h.to}
                                            onChange={(e) => setHour(d.id, { to: e.target.value })}
                                            className="min-w-0 flex-1 rounded-lg bg-black/60 border border-amber-500/20 px-1.5 py-1.5 text-xs text-white outline-none focus:border-amber-400/60 font-mono"
                                        />
                                    </div>
                                ) : (
                                    <span className="flex-1 text-xs italic text-slate-500">Fechado</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Card>

            <div className="flex items-center justify-end gap-3 pt-2">
                <button
                    data-testid="salon-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                    className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"} mr-2`}></i>
                    {saving ? "Salvando…" : "Salvar horários"}
                </button>
            </div>
        </div>
    );
};

const Kpi = ({ icon, label, value, accent, testId }) => {
    const accents = {
        amber: { ring: "border-amber-500/30", iconBg: "bg-amber-500/15 text-amber-300", val: "text-amber-200" },
        emerald: { ring: "border-emerald-500/30", iconBg: "bg-emerald-500/15 text-emerald-300", val: "text-emerald-200" },
        slate: { ring: "border-white/10", iconBg: "bg-slate-700/40 text-slate-300", val: "text-slate-100" },
    };
    const a = accents[accent] || accents.amber;
    return (
        <div data-testid={testId} className={`rounded-xl border ${a.ring} bg-black/50 p-4 flex items-center gap-3`}>
            <div className={`h-11 w-11 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                <i className={`fa-solid ${icon}`}></i>
            </div>
            <div className="min-w-0">
                <div className={`salon-heading text-2xl ${a.val}`}>{value}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 leading-tight">{label}</div>
            </div>
        </div>
    );
};

const FormField = ({ label, className = "", children }) => (
    <label className={`block ${className}`}>
        <span className="block text-[10px] uppercase tracking-[0.2em] text-amber-200/70 mb-1.5 font-bold">
            {label}
        </span>
        {children}
    </label>
);

// ─── Section: Minha Conta (contato do proprietário + PINs + plano) ────────
const MyAccountSection = ({ brand, updateBrand }) => {
    const isPJ = brand.ownerKind === "PJ";
    const [draft, setDraft] = useState({
        ownerName: brand.ownerName || "",
        responsiblePerson: brand.responsiblePerson || "",
        ownerEmail: brand.ownerEmail || "",
        ownerPhone: brand.ownerPhone || "",
    });

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const handleSave = () => {
        if (!draft.ownerName.trim()) {
            toast.error(isPJ ? "Informe o nome do proprietário." : "Informe seu nome completo.");
            return;
        }
        if (isPJ && !draft.responsiblePerson.trim()) {
            toast.error("PJ exige a pessoa responsável pelo sistema.");
            return;
        }
        if (!isValidEmail(draft.ownerEmail)) {
            toast.error("E-mail inválido.");
            return;
        }
        updateBrand(draft);
        toast.success("Dados atualizados", { description: "Cadastro salvo com sucesso." });
    };

    return (
        <div className="space-y-5">
            <Card title="Dados do Proprietário" desc="Informações de contato de quem responde pelo salão." icon="fa-user">
                <div className="grid md:grid-cols-2 gap-4">
                    <FieldInput
                        label={isPJ ? "Nome do proprietário" : "Nome completo"}
                        testId="account-owner-name"
                        value={draft.ownerName}
                        onChange={(v) => setField("ownerName", v)}
                        hint={isPJ ? "Sócio principal ou representante legal" : null}
                    />
                    {isPJ && (
                        <FieldInput
                            label="Responsável pelo sistema"
                            testId="account-responsible-person"
                            value={draft.responsiblePerson}
                            onChange={(v) => setField("responsiblePerson", v)}
                            hint="Quem opera o painel no dia a dia"
                        />
                    )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <FieldInput
                        label="E-mail de contato"
                        testId="account-email"
                        value={draft.ownerEmail}
                        onChange={(v) => setField("ownerEmail", v)}
                        type="email"
                    />
                    <FieldInput
                        label="Telefone / WhatsApp"
                        testId="account-phone"
                        value={draft.ownerPhone}
                        onChange={(v) => setField("ownerPhone", maskPhone(v))}
                        mono
                        placeholder="(00) 00000-0000"
                    />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                        data-testid="account-save-btn"
                        onClick={handleSave}
                        className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20"
                    >
                        <i className="fa-solid fa-floppy-disk mr-2"></i>Salvar alterações
                    </button>
                </div>
            </Card>

            <PinSection brand={brand} updateBrand={updateBrand} />
            <BillingSection brand={brand} updateBrand={updateBrand} />
        </div>
    );
};

// ─── Section: PINs de Segurança ─────────────────────────────────────────────
const PinSection = ({ brand, updateBrand }) => {
    const [kiosk, setKiosk] = useState(brand.kioskPin || "");
    const [fin, setFin] = useState(brand.financialPin || "");

    const handleSave = () => {
        if (!/^\d{4}$/.test(kiosk) || !/^\d{4}$/.test(fin)) {
            toast.error("Cada PIN precisa ter exatamente 4 dígitos numéricos.");
            return;
        }
        if (kiosk === fin) {
            toast.error("Use PINs diferentes para Kiosk e Financeiro (segurança).");
            return;
        }
        updateBrand({ kioskPin: kiosk, financialPin: fin });
        toast.success("PINs atualizados", { description: "As novas senhas valem a partir de agora." });
    };

    return (
        <Card
            title="PINs de Segurança Operacional"
            desc="4 dígitos numéricos cada. Use PINs diferentes para o Modo Kiosk e para o Painel Financeiro."
            icon="fa-lock"
        >
            <div className="grid md:grid-cols-2 gap-5">
                <PinField
                    label="PIN do Modo Kiosk"
                    desc="Trava a saída do Terminal Balcão (feature Imperial)"
                    icon="fa-expand"
                    value={kiosk}
                    onChange={setKiosk}
                    testId="account-pin-kiosk"
                />
                <PinField
                    label="PIN do Painel Financeiro"
                    desc="Libera acesso aos relatórios e comissões"
                    icon="fa-chart-line"
                    value={fin}
                    onChange={setFin}
                    testId="account-pin-financial"
                />
            </div>

            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-200/80 leading-relaxed">
                <i className="fa-solid fa-triangle-exclamation mr-2 text-amber-400"></i>
                Nunca compartilhe seus PINs por mensagens. Em caso de perda, redefina aqui mesmo —
                novos PINs entram em vigor imediatamente.
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
                <button
                    data-testid="account-pin-save-btn"
                    onClick={handleSave}
                    className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all shadow-lg shadow-amber-500/20"
                >
                    <i className="fa-solid fa-shield-halved mr-2"></i>Atualizar PINs
                </button>
            </div>
        </Card>
    );
};

const PinField = ({ label, desc, icon, value, onChange, testId }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-5">
            <div className="flex items-center gap-2 mb-3">
                <span className="h-8 w-8 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center">
                    <i className={`fa-solid ${icon}`}></i>
                </span>
                <div>
                    <div className="text-white font-semibold text-sm">{label}</div>
                    <div className="text-[11px] text-slate-500">{desc}</div>
                </div>
            </div>
            <div className="flex items-center rounded-xl border border-white/10 bg-slate-950/80 focus-within:border-amber-500/60 transition-colors">
                <input
                    data-testid={`${testId}-input`}
                    inputMode="numeric"
                    type={show ? "text" : "password"}
                    maxLength={4}
                    value={value}
                    onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="0000"
                    className="flex-1 bg-transparent px-4 py-3 text-white text-center font-mono text-2xl tracking-[0.6em] outline-none min-h-[56px]"
                />
                <button
                    type="button"
                    data-testid={`${testId}-toggle`}
                    onClick={() => setShow((s) => !s)}
                    className="px-3 text-slate-400 hover:text-white min-h-[44px] min-w-[44px]"
                >
                    <i className={`fa-solid ${show ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
            </div>
        </div>
    );
};

// ─── Section: Plano & Pagamento ─────────────────────────────────────────────
const BillingSection = ({ brand, updateBrand }) => {
    const [editingCard, setEditingCard] = useState(false);
    const [card, setCard] = useState(brand.paymentMethod || {});

    const planPrice = brand.plan === "Imperial" ? 699 : brand.plan === "Royal" ? 399 : 199;
    const nextBilling = brand.nextBillingDate
        ? new Date(brand.nextBillingDate).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
          })
        : "—";

    const saveCard = () => {
        if (!/^\d{4}$/.test(card.last4 || "")) {
            toast.error("Informe os últimos 4 dígitos do cartão.");
            return;
        }
        if (!card.holder || !card.expiry) {
            toast.error("Preencha nome do titular e validade.");
            return;
        }
        updateBrand({ paymentMethod: card });
        setEditingCard(false);
        toast.success("Cartão atualizado", { description: "A próxima cobrança usará o novo cartão." });
    };

    const changePlan = (newPlan) => {
        updateBrand({ plan: newPlan });
        toast.success(`Plano alterado para ${newPlan}`, {
            description: "Mudança efetiva no próximo ciclo de cobrança.",
        });
    };

    return (
        <div className="space-y-5">
            {/* Plan card */}
            <Card title="Plano atual" desc="Sua assinatura mensal da plataforma Kinng.app." icon="fa-crown">
                <div className="grid md:grid-cols-[1fr_auto] gap-5 items-center">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="salon-heading text-3xl text-white">{brand.plan}</span>
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] uppercase tracking-wider font-bold border border-emerald-500/30">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Ativo
                            </span>
                        </div>
                        <div className="text-sm text-slate-400 mt-1">
                            R$ <span className="font-mono text-white text-lg">{planPrice}</span>/mês ·
                            próxima cobrança em <span className="text-white">{nextBilling}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {["Classic", "Royal", "Imperial"].map((p) => (
                            <button
                                key={p}
                                data-testid={`account-plan-${p}`}
                                onClick={() => changePlan(p)}
                                className={`min-h-[40px] px-4 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                                    brand.plan === p
                                        ? "bg-amber-500 text-slate-950"
                                        : "border border-white/10 text-slate-300 hover:border-white/30 hover:text-white"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            </Card>

            {/* Payment method */}
            <Card title="Método de pagamento" desc="Cartão usado nas cobranças recorrentes." icon="fa-credit-card">
                {!editingCard ? (
                    <div className="flex items-center gap-5 flex-wrap">
                        <div className="relative w-72 h-44 rounded-2xl p-5 text-white shadow-2xl"
                            style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 60%, #312e81 100%)" }}
                            data-testid="account-current-card"
                        >
                            <div className="flex items-center justify-between">
                                <i className="fa-brands fa-cc-visa text-3xl text-white"></i>
                                <i className="fa-solid fa-wifi rotate-90 text-white/60"></i>
                            </div>
                            <div className="mt-8 font-mono text-lg tracking-[0.3em]">
                                •••• •••• •••• {card.last4 || "0000"}
                            </div>
                            <div className="mt-3 flex justify-between items-end">
                                <div>
                                    <div className="text-[9px] uppercase tracking-wider text-white/50">Titular</div>
                                    <div className="text-xs">{card.holder || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] uppercase tracking-wider text-white/50">Validade</div>
                                    <div className="text-xs font-mono">{card.expiry || "—"}</div>
                                </div>
                            </div>
                        </div>
                        <button
                            data-testid="account-edit-card-btn"
                            onClick={() => setEditingCard(true)}
                            className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-amber-500/60 text-slate-300 hover:text-amber-300 text-xs uppercase tracking-wider transition-all"
                        >
                            <i className="fa-solid fa-pen mr-2"></i>Trocar cartão
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4 max-w-2xl">
                        <FieldInput
                            label="Nome do titular (como no cartão)"
                            testId="account-card-holder"
                            value={card.holder || ""}
                            onChange={(v) => setCard({ ...card, holder: v.toUpperCase() })}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <FieldInput
                                label="Últimos 4 dígitos"
                                testId="account-card-last4"
                                value={card.last4 || ""}
                                onChange={(v) => setCard({ ...card, last4: v.replace(/\D/g, "").slice(0, 4) })}
                                mono
                                placeholder="0000"
                            />
                            <FieldInput
                                label="Validade (MM/AA)"
                                testId="account-card-expiry"
                                value={card.expiry || ""}
                                onChange={(v) => setCard({ ...card, expiry: v.replace(/[^\d/]/g, "").slice(0, 5) })}
                                mono
                                placeholder="12/27"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                data-testid="account-card-save-btn"
                                onClick={saveCard}
                                className="min-h-[48px] px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-wider text-xs"
                            >
                                Salvar cartão
                            </button>
                            <button
                                onClick={() => {
                                    setCard(brand.paymentMethod || {});
                                    setEditingCard(false);
                                }}
                                className="min-h-[48px] px-5 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-xs uppercase tracking-wider"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Billing history */}
            <Card title="Histórico de faturas" desc="Suas últimas cobranças pela plataforma." icon="fa-file-invoice-dollar">
                <div className="overflow-x-auto" data-testid="account-billing-history">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                <th className="text-left py-2 px-2">Fatura</th>
                                <th className="text-left py-2 px-2">Data</th>
                                <th className="text-right py-2 px-2">Valor</th>
                                <th className="text-center py-2 px-2">Status</th>
                                <th className="text-right py-2 px-2">Recibo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(brand.billingHistory || []).map((inv) => (
                                <tr key={inv.id} className="border-t border-white/5">
                                    <td className="py-3 px-2 font-mono text-slate-300">{inv.invoice}</td>
                                    <td className="py-3 px-2 text-slate-400">
                                        {new Date(inv.date).toLocaleDateString("pt-BR")}
                                    </td>
                                    <td className="py-3 px-2 text-right font-mono text-white">
                                        R$ {inv.amount.toFixed(2)}
                                    </td>
                                    <td className="py-3 px-2 text-center">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] uppercase tracking-wider font-bold border border-emerald-500/30">
                                            <i className="fa-solid fa-check text-[8px]"></i>Pago
                                        </span>
                                    </td>
                                    <td className="py-3 px-2 text-right">
                                        <button
                                            data-testid={`account-invoice-${inv.id}`}
                                            onClick={() =>
                                                toast.success("Recibo enviado", {
                                                    description: `PDF gerado e disponível em /invoices/${inv.invoice}.pdf`,
                                                })
                                            }
                                            className="h-9 w-9 rounded-lg bg-white/5 hover:bg-amber-500/20 hover:text-amber-300 text-slate-400 transition-all"
                                        >
                                            <i className="fa-solid fa-download text-xs"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

// ─── Reusable ───────────────────────────────────────────────────────────────
const Card = ({ title, desc, icon, children }) => (
    <section className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4 sm:p-6 md:p-8 space-y-5">
        <header className="flex items-start gap-3 pb-4 border-b border-white/5">
            <div className="h-11 w-11 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
                <i className={`fa-solid ${icon}`}></i>
            </div>
            <div>
                <h3 className="salon-heading text-xl text-white">{title}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
            </div>
        </header>
        {children}
    </section>
);

const Label = ({ children }) => (
    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2">{children}</div>
);

const FieldInput = ({ label, value, onChange, testId, placeholder, type = "text", mono, hint }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
        <input
            data-testid={testId}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`mt-1.5 w-full bg-slate-950/60 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-amber-500/60 transition-colors min-h-[48px] ${
                mono ? "font-mono" : ""
            }`}
        />
        {hint && <span className="text-[11px] text-slate-500 mt-1 block">{hint}</span>}
    </label>
);

export default Account;
